/**
 * Étape 2 — Type de logement.
 *
 * Influence l'éligibilité aux programmes :
 *  - Maison individuelle → MPR Décarbonation/Parcours, CEE BAR-TH/BAR-EN
 *  - Appartement → idem (privatif) ou via copro pour les communs
 *  - Copropriété entière → MPR Copro + CEE collectifs
 */

import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  Home,
  Info,
  Loader2,
  Search,
  Sparkles,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { aidsStepCounterLabel, useAidsStore, type LogementType } from './useAidsStore';

interface LogementMeta {
  id: LogementType;
  label: string;
  sublabel: string;
  icon: LucideIcon;
  features: string[];
}

const LOGEMENTS: LogementMeta[] = [
  {
    id: 'maison',
    label: 'Maison individuelle',
    sublabel: 'Pavillon, villa, mitoyen',
    icon: Home,
    features: ['Tous travaux éligibles', 'MPR plein barème'],
  },
  {
    id: 'appartement',
    label: 'Appartement',
    sublabel: 'Logement en immeuble',
    icon: Building2,
    features: ['Travaux privatifs', 'PAC air/air autorisée'],
  },
  {
    id: 'copropriete',
    label: 'Copropriété (parties communes)',
    sublabel: 'Travaux votés en AG',
    icon: Users,
    features: ['MPR Copro', 'CEE collectifs bonifiés'],
  },
];

const DPE_CLASSES = ['G', 'F', 'E', 'D', 'C', 'B', 'A'] as const;

const DPE_COLORS: Record<string, string> = {
  G: 'bg-red-600 text-white',
  F: 'bg-orange-500 text-white',
  E: 'bg-amber-400 text-ink',
  D: 'bg-yellow-300 text-ink',
  C: 'bg-lime-400 text-ink',
  B: 'bg-green-500 text-white',
  A: 'bg-emerald-600 text-white',
};

/* Type retourné par /api/dpe/search */
interface DpeSearchResult {
  id: string;
  address: string;
  dpeClass: string;
  gesClass: string | null;
  energyConsumptionKwhM2: number | null;
  surfaceHabitable: number | null;
  anneeConstruction: number | null;
  typeLogement: string | null;
  dateDpe: string | null;
  version: '2021' | 'legacy' | null;
}

export function Step2Logement() {
  const profile = useAidsStore((s) => s.profile);
  const logement = useAidsStore((s) => s.logement);
  const dpeActuel = useAidsStore((s) => s.dpeActuel);
  const energyBillEur = useAidsStore((s) => s.energyBillEur);
  const setLogement = useAidsStore((s) => s.setLogement);
  const setDpeActuel = useAidsStore((s) => s.setDpeActuel);
  const setEnergyBillEur = useAidsStore((s) => s.setEnergyBillEur);
  const goNext = useAidsStore((s) => s.goNext);

  const [showOptional, setShowOptional] = useState(!!(dpeActuel || energyBillEur));

  // Recherche DPE ADEME
  const [dpeAddress, setDpeAddress] = useState('');
  const [dpeSearching, setDpeSearching] = useState(false);
  const [dpeResults, setDpeResults] = useState<DpeSearchResult[]>([]);
  const [dpeError, setDpeError] = useState<string | null>(null);
  const [dpeSearchDone, setDpeSearchDone] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchDpe = useCallback(async (address: string) => {
    if (address.trim().length < 5) return;
    setDpeSearching(true);
    setDpeError(null);
    setDpeSearchDone(false);
    try {
      const params = new URLSearchParams({ q: address, limit: '6' });
      const location = useAidsStore.getState().location;
      if (location?.code) params.set('codeInsee', location.code);
      const res = await fetch(`/api/dpe/search?${params.toString()}`);
      if (!res.ok) throw new Error('Erreur API');
      const json = (await res.json()) as { results: DpeSearchResult[] };
      setDpeResults(json.results ?? []);
      setDpeSearchDone(true);
    } catch {
      setDpeError('Impossible de rechercher les DPE. Saisissez votre classe manuellement.');
      setDpeResults([]);
    } finally {
      setDpeSearching(false);
    }
  }, []);

  function handleDpeAddressChange(value: string) {
    setDpeAddress(value);
    setDpeSearchDone(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length >= 5) {
      debounceRef.current = setTimeout(() => void searchDpe(value), 700);
    }
  }

  function selectDpeResult(result: DpeSearchResult) {
    setDpeActuel(result.dpeClass);
    if (result.energyConsumptionKwhM2) {
      // Estimation facture énergie si surface connue et conso/m² connue
      // On ne set pas automatiquement pour ne pas induire en erreur
    }
    setDpeResults([]);
    setDpeSearchDone(false);
    setDpeAddress(result.address);
  }

  // Si copropriété : on n'affiche que les options compatibles
  const options =
    profile === 'copropriete'
      ? LOGEMENTS.filter((l) => l.id === 'copropriete' || l.id === 'appartement')
      : LOGEMENTS;

  function onSelect(id: LogementType) {
    setLogement(id);
    // Petit délai pour laisser l'animation de sélection se voir
    window.setTimeout(() => goNext(), 180);
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {aidsStepCounterLabel(1, profile)}
      </p>
      <h2 className="mt-2 h-section">
        Votre logement, c'est…
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Le type de logement détermine la nature des travaux finançables et le
        circuit d'instruction du dossier (Anah individuelle ou syndicat).
      </p>

      <fieldset className="mt-6 border-0 p-0">
        <legend className="sr-only">Type de logement</legend>
        <div role="radiogroup" aria-label="Type de logement" className="space-y-2.5">
          {options.map((l) => {
            const Icon = l.icon;
            const selected = logement === l.id;
            return (
              <button
                key={l.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onSelect(l.id)}
                className={cn(
                  'group relative flex w-full items-start gap-4 overflow-hidden rounded-xl border-2 bg-white p-4 text-left transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                  selected
                    ? 'border-brand-500 bg-brand-50/70 shadow-[0_4px_24px_-8px_rgba(16,185,129,0.25)]'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50/60',
                )}
              >
                {/* Glow d'arrière-plan animé quand sélectionné */}
                {selected && (
                  <motion.span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-50/0 via-brand-50/50 to-accent-50/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, ease: easeStripe }}
                  />
                )}

                <span
                  className={cn(
                    'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-150',
                    selected
                      ? 'bg-brand-100 text-brand-700'
                      : 'bg-stone-100 text-stone-600',
                  )}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>

                <div className="relative min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-[14.5px] font-semibold',
                      selected ? 'text-brand-900' : 'text-ink',
                    )}
                  >
                    {l.label}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-stone-500">
                    {l.sublabel}
                  </p>
                  <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    {l.features.map((f) => (
                      <li
                        key={f}
                        className="inline-flex items-center gap-1 text-[11px] text-stone-500"
                      >
                        <CheckCircle2
                          className={cn(
                            'h-3 w-3',
                            selected ? 'text-brand-500' : 'text-stone-300',
                          )}
                          strokeWidth={2}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <ArrowRight
                  className={cn(
                    'relative mt-2 h-4 w-4 shrink-0 transition-all duration-150',
                    selected
                      ? 'translate-x-0.5 text-brand-500'
                      : 'text-stone-300 group-hover:translate-x-0.5',
                  )}
                />
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* ── DPE + facture (optionnels, enrichissent le résultat) ── */}
      <div className="mt-5">
        <button
          type="button"
          onClick={() => setShowOptional((v) => !v)}
          className="flex w-full items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-stone-50/60 px-4 py-3 text-left text-[12.5px] text-stone-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700"
        >
          <Zap className="h-4 w-4 shrink-0 text-brand-500" strokeWidth={1.7} />
          <span className="flex-1">
            {dpeActuel
              ? `DPE actuel : classe ${dpeActuel} — modifier ou ajouter la facture énergie`
              : 'Ajouter votre DPE et votre facture énergie pour un résultat plus précis'}
          </span>
          {dpeActuel && (
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[12px] font-black',
                DPE_COLORS[dpeActuel],
              )}
            >
              {dpeActuel}
            </span>
          )}
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', showOptional && 'rotate-180')}
            strokeWidth={2}
          />
        </button>

        {showOptional && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: easeStripe }}
            className="mt-3 space-y-4 rounded-xl border border-stone-200 bg-white p-4"
          >
            {/* Recherche DPE ADEME par adresse */}
            <div>
              <label
                htmlFor="dpe-address-search"
                className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-stone-700"
              >
                <Sparkles className="h-3.5 w-3.5 text-brand-500" strokeWidth={2} />
                Rechercher votre DPE officiel (base ADEME)
                <span className="font-normal text-stone-400">— optionnel</span>
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400"
                  strokeWidth={1.7}
                />
                <input
                  id="dpe-address-search"
                  type="text"
                  autoComplete="street-address"
                  placeholder="Saisissez votre adresse exacte (ex: 12 rue de la Paix 75001 Paris)"
                  value={dpeAddress}
                  onChange={(e) => handleDpeAddressChange(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 bg-stone-50 py-2 pl-9 pr-10 text-[12.5px] outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-100"
                />
                {dpeSearching && (
                  <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-brand-500" />
                )}
              </div>
              <p className="mt-1 text-[10.5px] text-stone-400">
                Recherche dans la base officielle ADEME (data.gouv.fr). Votre adresse n'est pas transmise à des tiers.
              </p>

              {/* Résultats DPE */}
              {dpeError && (
                <p className="mt-2 text-[11px] text-red-600">{dpeError}</p>
              )}
              {dpeResults.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-[10.5px] font-semibold text-stone-500">
                    {dpeResults.length} DPE trouvé{dpeResults.length > 1 ? 's' : ''} — sélectionnez le vôtre :
                  </p>
                  {dpeResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectDpeResult(r)}
                      className={cn(
                        'group flex w-full items-center gap-3 rounded-lg border bg-white px-3 py-2.5 text-left transition-all',
                        dpeActuel === r.dpeClass && dpeAddress === r.address
                          ? 'border-brand-400 ring-2 ring-brand-100'
                          : 'border-stone-200 hover:border-brand-300 hover:bg-brand-50/30',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[14px] font-black',
                          DPE_COLORS[r.dpeClass] ?? 'bg-stone-200 text-stone-700',
                        )}
                      >
                        {r.dpeClass}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-semibold text-stone-800">{r.address}</p>
                        <div className="mt-0.5 flex flex-wrap gap-2 text-[10.5px] text-stone-400">
                          {r.surfaceHabitable && <span>{r.surfaceHabitable} m²</span>}
                          {r.anneeConstruction && <span>construit en {r.anneeConstruction}</span>}
                          {r.energyConsumptionKwhM2 && (
                            <span>{r.energyConsumptionKwhM2} kWh/m²/an</span>
                          )}
                          {r.typeLogement && <span className="capitalize">{r.typeLogement}</span>}
                          {r.version === 'legacy' && (
                            <span className="text-amber-600">DPE pré-2021 (indicatif)</span>
                          )}
                        </div>
                      </div>
                      <CheckCircle2
                        className={cn(
                          'h-4 w-4 shrink-0 transition-colors',
                          dpeActuel === r.dpeClass ? 'text-brand-500' : 'text-stone-200 group-hover:text-stone-300',
                        )}
                        strokeWidth={2}
                      />
                    </button>
                  ))}
                </div>
              )}

              {dpeSearchDone && dpeResults.length === 0 && !dpeError && (
                <p className="mt-2 text-[11px] text-stone-400">
                  Aucun DPE trouvé pour cette adresse. Sélectionnez la classe manuellement ci-dessous.
                </p>
              )}
            </div>

            {/* Sélection manuelle de la classe DPE */}
            <div>
              <label className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-stone-700">
                Classe DPE actuelle
                {!dpeActuel && <span className="font-normal text-stone-400">(saisie manuelle)</span>}
                <button
                  type="button"
                  title="Mentionnée sur votre diagnostic de performance énergétique ou votre bail"
                  className="ml-1 text-stone-300 hover:text-stone-500"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DPE_CLASSES.map((cls) => (
                  <button
                    key={cls}
                    type="button"
                    onClick={() => setDpeActuel(dpeActuel === cls ? null : cls)}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg text-[14px] font-black transition-all',
                      dpeActuel === cls
                        ? DPE_COLORS[cls]
                        : 'border-2 border-stone-200 bg-white text-stone-500 hover:border-stone-300',
                    )}
                  >
                    {cls}
                  </button>
                ))}
                {dpeActuel && (
                  <button
                    type="button"
                    onClick={() => setDpeActuel(null)}
                    className="text-[11px] text-stone-400 underline underline-offset-2 hover:text-stone-600"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>

            {/* Facture annuelle */}
            <div>
              <label
                htmlFor="energy-bill"
                className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-stone-700"
              >
                Facture énergie annuelle
                <span className="font-normal text-stone-400">(€/an, optionnel)</span>
              </label>
              <div className="relative">
                <input
                  id="energy-bill"
                  type="number"
                  inputMode="decimal"
                  min={100}
                  max={50000}
                  placeholder="Ex : 2 400"
                  value={energyBillEur ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    setEnergyBillEur(v && !isNaN(v) ? v : null);
                  }}
                  className="w-full rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 pr-10 text-[13px] outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-100"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-stone-400">
                  €/an
                </span>
              </div>
              <p className="mt-1 text-[11px] text-stone-400">
                Cumul de toutes vos factures énergie (électricité, gaz, fioul…) sur 12 mois.
              </p>
            </div>
          </motion.div>
        )}
      </div>

      <div className="mt-7 flex justify-end">
        <Button
          variant="ghost"
          size="lg"
          disabled={logement === null}
          onClick={goNext}
          className="gap-2 text-stone-500"
        >
          Continuer sans modifier
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
